import { VaultObjectViewerBackground } from "@/components/vault/viewer/VaultObjectViewerBackground";
import {
  LoaderData,
  VaultObjectViewerData,
} from "@/components/vault/viewer/VaultObjectViewerModal";
import { VaultObjectViewerSticker } from "@/components/vault/viewer/VaultObjectViewerSticker";
import { TransformControls } from "@/engine/viewer/loaders/TransformControls";
import Viewer from "@/engine/viewer/Viewer";
import { useScreenStore } from "@/stores/useScreenStore";
import useVaultFilterStore from "@/stores/zustand/vault/useVaultFilterStore";
import useVaultStore from "@/stores/zustand/vault/useVaultStore";
import React, { useEffect, useRef, useState } from "react";
import { VaultObjectThumbnailLights } from "@/utils/filterPreset";
import { AnnotationAddButton } from "@/components/vault/annotation/AnnotationAddButton";
import { VaultObject } from "@/components/vault/viewer/VaultObject";

const VaultObjectUploadCaptureHiddenHengeLazyComponent = React.lazy(
  () => import("@/engine/henge/VaultObjectUploadCaptureHiddenHenge"),
);

interface VaultObjectViewerProps {
  loaderData: LoaderData | null;
  onCloseModal: () => void;
  vaultObjectViewerData: VaultObjectViewerData | null;
}

export const VaultObjectViewer = ({
  loaderData,
  onCloseModal,
  vaultObjectViewerData,
}: VaultObjectViewerProps) => {
  const isViewerOpen = useVaultStore((state) => state.isViewerOpen);
  const currentPage = useVaultStore((state) => state.currentPage);
  const canvasStatus = useVaultStore((state) => state.canvasStatus);
  const {
    updateVaultObjectViewerTextColor,
    setCanvasStatus,
    updateHoverPoint,
  } = useVaultStore((state) => state.vaultStoreActions);

  const currentFilter = useVaultFilterStore((state) => state.currentFilter);

  const wrapperRef = useRef<HTMLDivElement>(null!);
  const logoRef = useRef<HTMLDivElement>(null!);
  const titleRef = useRef<HTMLDivElement>(null!);
  const panelRef = useRef<HTMLDivElement>(null!);
  const actionButtonRef = useRef<HTMLDivElement>(null!);
  const inspectorRef = useRef<HTMLDivElement>(null!);
  const annotationAddButtonRef = useRef<HTMLDivElement>(null!);
  useEffect(() => {
    function prevent(e: MouseEvent) {
      e.preventDefault();
      return false;
    }
    logoRef.current?.addEventListener("contextmenu", prevent);
    titleRef.current?.addEventListener("contextmenu", prevent);
    panelRef.current?.addEventListener("contextmenu", prevent);
    actionButtonRef.current?.addEventListener("contextmenu", prevent);

    return () => {
      logoRef.current?.removeEventListener("contextmenu", prevent);
      titleRef.current?.removeEventListener("contextmenu", prevent);
      panelRef.current?.removeEventListener("contextmenu", prevent);
      actionButtonRef.current?.removeEventListener("contextmenu", prevent);
    };
  }, [
    logoRef.current,
    titleRef.current,
    panelRef.current,
    actionButtonRef.current,
  ]);
  const observer = useRef<ResizeObserver | undefined>(undefined);

  const [wrapperSize, setWrapperSize] = useState<{
    width: number;
    height: number;
  }>();

  useEffect(() => {
    if (!wrapperRef.current) return;
    if (observer.current) return;

    observer.current = new ResizeObserver((entries: ResizeObserverEntry[]) => {
      if (entries.length > 0) {
        const entry = entries[0];
        if (entry.borderBoxSize) {
          setWrapperSize({
            width: entry.borderBoxSize[0].inlineSize,
            height: entry.borderBoxSize[0].blockSize,
          });
        }
      }
    });

    observer.current.observe(wrapperRef.current);
  }, [wrapperRef.current]);

  const [resetCamera, setResetCamera] = useState(false);

  //================================================================

  const transformControlsRef = useRef<TransformControls | undefined>(undefined);
  const [currentTransformControlsMode, setCurrentTransformControlsMode] =
    useState<"translate" | "rotate" | null>(null);

  useEffect(() => {
    if (!transformControlsRef.current) return;

    if (currentTransformControlsMode) {
      transformControlsRef.current.mode = currentTransformControlsMode;
      transformControlsRef.current.space =
        currentTransformControlsMode === "rotate" ? "local" : "world";
    }
    transformControlsRef.current.showX = !!currentTransformControlsMode;
    transformControlsRef.current.showY = !!currentTransformControlsMode;
    transformControlsRef.current.showZ = !!currentTransformControlsMode;
  }, [currentTransformControlsMode]);
  const isMobileScreen = useScreenStore((state) => state.isMobileScreen);
  const [captureReady, setCaptureReady] = useState(false);
  const [warnThumbnailClipping, setWarnThumbnailClipping] = useState(false);

  useEffect(() => {
    setCaptureReady(false);
    updateHoverPoint(null);
  }, [loaderData]);

  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [uploadFailed, setUploadFailed] = useState(false);

  useEffect(() => {
    setUploadLoading(false);
    setUploadFailed(false);
    setUploadComplete(false);
  }, [currentPage]);

  useEffect(() => {
    setCanvasStatus("idle");

    if (isViewerOpen && loaderData) {
      setCanvasStatus("loading");
    }
  }, [isViewerOpen, loaderData, vaultObjectViewerData]);
  useEffect(() => {
    if (uploadLoading || uploadComplete || uploadFailed) {
      setCanvasStatus("hidden");
    }
  }, [uploadLoading, uploadComplete, uploadFailed]);

  // 로딩 여부, 필터 백그라운드 색상에 따라 텍스트 색상 동적으로 업데이트
  useEffect(() => {
    updateVaultObjectViewerTextColor(canvasStatus === "loading");
  }, [canvasStatus, currentFilter]);

  return (
    <>
      <div
        ref={wrapperRef}
        style={{
          width:
            (vaultObjectViewerData?.embed || isMobileScreen) &&
            !currentPage.endsWith("thumbnail")
              ? "100vw"
              : "auto",
          maxWidth:
            (vaultObjectViewerData?.embed || isMobileScreen) &&
            !currentPage.endsWith("thumbnail")
              ? "100vw"
              : "80vw",
          height:
            (vaultObjectViewerData?.embed || isMobileScreen) &&
            !currentPage.endsWith("thumbnail")
              ? "100vh"
              : "80vh",
          aspectRatio:
            (vaultObjectViewerData?.embed || isMobileScreen) &&
            !currentPage.endsWith("thumbnail")
              ? "auto"
              : "16 / 10",
        }}
      >
        <VaultObjectViewerBackground
          uploadLoading={uploadLoading}
          uploadComplete={uploadComplete}
          uploadFailed={uploadFailed}
        />

        <div
          style={{
            visibility:
              canvasStatus === "render" || canvasStatus === "error"
                ? "visible"
                : "hidden",
            transform:
              // currentPage.endsWith("thumbnail")
              // ? "translate(-50%, -67%)"
              // :
              "translate(-50%, -50%)",
            width:
              // currentPage.endsWith("thumbnail") && wrapperSize
              //   ? `${40 * Math.max(1, wrapperSize.width / wrapperSize.height)}vh`
              //   :
              "100vw",
            height:
              // currentPage.endsWith("thumbnail") && wrapperSize
              //   ? `${40 * Math.max(1, wrapperSize.width / wrapperSize.height)}vh`
              //   :
              "100vh",
            // background: "rgba(0, 0, 0, 0.2)",
          }}
          className={"absolute top-1/2 left-1/2 z-10"}
        >
          <Viewer
            id={"vault-object-viewer"}
            onDoubleClick={() => setResetCamera(true)}
            filter={currentFilter}
            lights={
              currentPage.endsWith("thumbnail")
                ? [
                    ...currentFilter.lightPreset.lights.map((light) => ({
                      ...light,
                      castShadow: false,
                    })),
                    ...VaultObjectThumbnailLights,
                  ]
                : currentFilter.lightPreset.lights
            }
          >
            <>
              {loaderData && (
                <VaultObject
                  loaderData={loaderData}
                  resetCamera={{
                    trigger: resetCamera,
                    callback: () => setResetCamera(false),
                  }}
                  transformControlsRef={transformControlsRef}
                  occludeTarget={[logoRef, titleRef, panelRef, actionButtonRef]}
                  wrapperSize={wrapperSize}
                  setWarnThumbnailClipping={setWarnThumbnailClipping}
                />
              )}
            </>
            <>
              {/*{currentPage === "uploader" && currentTab === "metadata" (*/}
              <AnnotationAddButton portalRef={annotationAddButtonRef} />
              {/*)}*/}
            </>
          </Viewer>
        </div>

        <div
          style={{
            background: "rgba(0, 0, 0, 0.3)",
          }}
          className={
            "absolute top-[9999px] left-[9999px] w-[1024px] h-[1024px] z-10"
          }
        >
          {currentPage.endsWith("thumbnail") && (
            <Viewer
              id={"vault-object-thumbnail-hidden-viewer"}
              filter={currentFilter}
              lights={
                currentPage.endsWith("thumbnail")
                  ? [
                      ...currentFilter.lightPreset.lights.map((light) => ({
                        ...light,
                        castShadow: false,
                      })),
                      ...VaultObjectThumbnailLights,
                    ]
                  : currentFilter.lightPreset.lights
              }
            >
              <>
                {loaderData && (
                  <VaultObjectUploadCaptureHiddenHengeLazyComponent
                    loaderData={loaderData}
                    onLoad={() => setCaptureReady(true)}
                  />
                )}
              </>
            </Viewer>
          )}
        </div>
        {loaderData && (
          <VaultObjectViewerSticker
            onCloseModal={onCloseModal}
            logoRef={logoRef}
            titleRef={titleRef}
            panelRef={panelRef}
            actionButtonRef={actionButtonRef}
            loaderData={loaderData}
            canvasStatus={canvasStatus}
            vaultObjectViewerData={vaultObjectViewerData}
            wrapperSize={wrapperSize}
            setCurrentTransformControlsMode={setCurrentTransformControlsMode}
            captureReady={captureReady}
            uploadLoading={uploadLoading}
            setUploadLoading={setUploadLoading}
            uploadComplete={uploadComplete}
            setUploadComplete={setUploadComplete}
            uploadFailed={uploadFailed}
            setUploadFailed={setUploadFailed}
            warnThumbnailClipping={warnThumbnailClipping}
          />
        )}

        <div
          ref={inspectorRef}
          className={
            "pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-screen h-screen z-30"
          }
        />
      </div>
    </>
  );
};
